import { useNavigate } from "react-router-dom";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import useQuery from "@/hooks/useQuery";
import { NOTIFICATIONS_FETCH_URL } from "@/common/constants";
import { formatNotificationDate } from "./formatNotificationDate";
import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

interface ApiNotification {
  id: string;
  event_type: string;
  title: string;
  body: string;
  metadata: {
    total_amount?: string;
    amount_due?: string;
    order_id?: string;
    invoice_id?: string;
    estimated_delivery_range?: string;
  };
  created_at: string;
  read_at: string | null;
}

export default function NotificationsCard(props?: { onClose?: () => void }) {
  const { data: notificationsData } = useQuery(NOTIFICATIONS_FETCH_URL);
  const [notifications, setNotifications] = useState<ApiNotification[]>([]);

  useEffect(() => {
    if (notificationsData && Array.isArray(notificationsData)) {
      setNotifications(notificationsData as ApiNotification[]);
    }
  }, [notificationsData]);

  const navigate = useNavigate();
  const apiCall = useApiCallHandling();

  const handleMarkAllAsRead = async () => {
    await apiCall(
      `/api/v1/notifications/read-all`,
      "POST",
      {},
      () => {},
      () => {},
      () => {},
    );
  };

  const handleMarkAsRead = async (id: string) => {
    await apiCall(
      `/api/v1/notifications/${id}/mark-as-read`,
      "POST",
      {},
      () => console.log(`Notification ${id} marked as read`),
      () => {},
      () => {},
    );
  };

  const handleDismiss = (id: string) => {
    setNotifications(notifications.filter((notif) => notif.id !== id));
  };

  return (
    <div className="w-100 bg-white rounded-lg shadow-lg p-4 border border-gray-200 z-9999">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-semibold text-gray-900">Notifications</h2>

        {notifications.length > 0 && (
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            Mark all as read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="py-6 text-center">
          <p className="text-sm text-gray-500">No notifications</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {notifications.map((n) => {
              const isUnread = !n.read_at;

              return (
                <div
                  key={n.id}
                  onClick={() => isUnread && handleMarkAsRead(n.id)}
                  className={`group cursor-pointer p-4 border-b transition-all duration-200
                    ${isUnread ? "bg-blue-50" : "bg-white"}
                    hover:bg-blue-100`}
                >
                  <div className="flex justify-between items-start gap-2">
                    {/* LEFT CONTENT */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        {isUnread && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 animate-pulse" />
                        )}
                        <h4 className="text-sm font-semibold text-gray-800">
                          {n.title}
                        </h4>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                        {n.body}
                      </p>
                      {n.metadata?.total_amount || n.metadata?.amount_due ? (
                        <p className="text-xs text-gray-500 mt-0.5">
                          ₦
                          {Number(
                            n.metadata.total_amount ?? n.metadata.amount_due,
                          ).toLocaleString()}
                        </p>
                      ) : null}
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 mt-2 block">
                          {formatNotificationDate(n.created_at)}
                        </span>
                        {isUnread && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(n.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-sm text-blue-600 hover:underline flex-shrink-0 justify-end"
                          >
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDismiss(n.id);
                      }}
                      className="text-gray-400 hover:text-red-600 transition flex-shrink-0 opacity-0 group-hover:opacity-100"
                    >
                      <FiX size={18} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <button
            onClick={() => {
              navigate("/dashboard/notifications");
              props?.onClose?.();
            }}
            className="w-full mt-4 py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition font-medium"
          >
            View all notifications →
          </button>
        </>
      )}
    </div>
  );
}
